<script setup lang="ts">
import { computed, ref } from 'vue';
import { useFileManagerSettings } from '../../logic/core/useFileManagerSettings';
import type { PoolView, SidebarView } from './types';

const props = defineProps<{
  sidebar: SidebarView;
  views: PoolView[];
}>();

const {
  listWidthPx,
  poolWeights,
  resizeSidebar,
  resizePoolPanes,
  persistLayout,
} = useFileManagerSettings();

const rootRef = ref<HTMLElement | null>(null);
const activeSplitter = ref<string | null>(null);
const paneRefs = new Map<string, HTMLElement>();

const setPaneRef = (id: string, el: Element | null) => {
  if (el instanceof HTMLElement) {
    paneRefs.set(id, el);
  } else {
    paneRefs.delete(id);
  }
};

type PoolItem =
  | { type: 'pane'; key: string; view: PoolView }
  | { type: 'splitter'; key: string; idA: string; idB: string };

const poolItems = computed<PoolItem[]>(() => {
  const items: PoolItem[] = [];
  props.views.forEach((view, index) => {
    if (index > 0) {
      const idA = props.views[index - 1].id;
      items.push({
        type: 'splitter',
        key: `splitter-${idA}-${view.id}`,
        idA,
        idB: view.id,
      });
    }
    items.push({ type: 'pane', key: view.id, view });
  });
  return items;
});

const onSidebarPointerDown = (event: PointerEvent) => {
  const target = event.currentTarget as HTMLElement;
  target.setPointerCapture(event.pointerId);
  activeSplitter.value = 'sidebar';
  let lastX = event.clientX;

  const onPointerMove = (moveEvent: PointerEvent) => {
    const deltaPx = moveEvent.clientX - lastX;
    lastX = moveEvent.clientX;
    const containerWidthPx = rootRef.value?.getBoundingClientRect().width ?? 0;
    resizeSidebar(deltaPx, containerWidthPx);
  };

  const onPointerUp = () => {
    target.releasePointerCapture(event.pointerId);
    target.removeEventListener('pointermove', onPointerMove);
    target.removeEventListener('pointerup', onPointerUp);
    activeSplitter.value = null;
    persistLayout();
  };

  target.addEventListener('pointermove', onPointerMove);
  target.addEventListener('pointerup', onPointerUp);
};

const onPoolPointerDown = (
  event: PointerEvent,
  idA: string,
  idB: string,
  key: string,
) => {
  const startRectA = paneRefs.get(idA)?.getBoundingClientRect();
  const startRectB = paneRefs.get(idB)?.getBoundingClientRect();
  if (!startRectA || !startRectB) return;

  const target = event.currentTarget as HTMLElement;
  target.setPointerCapture(event.pointerId);
  activeSplitter.value = key;
  const startX = event.clientX;

  const onPointerMove = (moveEvent: PointerEvent) => {
    const deltaPx = moveEvent.clientX - startX;
    resizePoolPanes(idA, idB, deltaPx, startRectA, startRectB);
  };

  const onPointerUp = () => {
    target.releasePointerCapture(event.pointerId);
    target.removeEventListener('pointermove', onPointerMove);
    target.removeEventListener('pointerup', onPointerUp);
    activeSplitter.value = null;
    persistLayout();
  };

  target.addEventListener('pointermove', onPointerMove);
  target.addEventListener('pointerup', onPointerUp);
};
</script>

<template>
  <div ref="rootRef" class="view-layout">
    <div class="sidebar" :style="{ width: `${listWidthPx}px` }">
      <div class="view-content">
        <component :is="sidebar.component" v-bind="sidebar.props" />
      </div>
    </div>
    <div
      class="splitter"
      :class="{ active: activeSplitter === 'sidebar' }"
      @pointerdown="onSidebarPointerDown"
    >
      <div class="splitter-line" />
      <div class="splitter-hit-area" />
    </div>
    <div class="pool">
      <template v-for="item in poolItems" :key="item.key">
        <div
          v-if="item.type === 'splitter'"
          class="splitter"
          :class="{ active: activeSplitter === item.key }"
          @pointerdown="onPoolPointerDown($event, item.idA, item.idB, item.key)"
        >
          <div class="splitter-line" />
          <div class="splitter-hit-area" />
        </div>
        <div
          v-else
          :ref="(el) => setPaneRef(item.view.id, el as Element | null)"
          class="pool-pane"
          :style="{ flex: `${poolWeights[item.view.id] ?? 0} 1 0%` }"
        >
          <div class="view-content">
            <component :is="item.view.component" v-bind="item.view.props" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.view-layout {
  display: flex;
  flex: 1;
  min-height: 0;
}

.sidebar {
  flex: 0 0 auto;
}

.pool {
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
}

.pool-pane {
  min-width: 240px;
  min-height: 0;
}

.view-content {
  height: 100%;
  overflow: hidden;
  background-color: #fff;
  border-radius: 0.75rem;
}

.splitter {
  display: grid;
  place-items: center;
  flex: 0 0 1rem;
  pointer-events: none;
}

.splitter-line {
  grid-area: 1 / 1;
  width: 2px;
  height: 100%;
  background-color: transparent;
  transition: background-color 0.15s ease;
}

.splitter-hit-area {
  grid-area: 1 / 1;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  pointer-events: auto;
}

.splitter:hover .splitter-line,
.splitter.active .splitter-line {
  background-color: var(--primary-color);
}
</style>
